import { DataLakeEnums } from 'dashboard-services/api/dataLake';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { ReduxService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions, statusBoxActions } from 'primary-components';

import basicCustomInsightActions from './basic.js';
import basicCustomInsightConsts from './basicConsts.js';

import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { unparse } from 'papaparse';

export default (() => {
  const { TILE_TYPES } = basicCustomInsightConsts

  const isWorkflow = content => {
    if(content.tiles?.some(tile => basicCustomInsightConsts.WORKFLOW_TILES.includes(tile.type))) {
      return true;
    }
    if(content.tiles?.some(tile => tile.actions?.length > 0)) {
      return true;
    }
    if(content?.footer?.buttons.some(button => button.type === basicCustomInsightConsts.BUTTON_TYPES.DOWNLOAD_DATA_PREP)) {
      return true;
    }
    return false;
  }

  const updateInfoFields = ({ fields, name, value }) => {
    const index = fields.findIndex(field => field.name === name)
    let newFields;
    if(index > -1) {
      newFields = fields.updateInList({ value }, index)
    } else {
      newFields = fields.add({ value, name }, index)
    }
    return newFields
  }

  const onValidationHandler = ({ response, setErrors }) => dispatch => {
    const errors = response.validationErrors
    if(setErrors !== undefined) {
      const formErrors = (errors || []).filter((e = {}) => e.field === "name" || e.field === "groupName" || e.field === "description" || e.field === "image" || (String(e.field).includes("tags")))
      const differentErrors = (errors || []).filter((e = {}) => e.field !== "name" || e.field !== "groupName" || e.field !== "description" || e.field !== "image" && !(String(e.field).includes("tags")))
      if(differentErrors && differentErrors.length > 0) {
        differentErrors.forEach(e => dispatch(globalActions.getMessageStore()).error(e.message))
      }
      setErrors(formErrors)
    } else {
      errors?.forEach(e => dispatch(globalActions.getMessageStore()).error(e.message))
    }
  }

  const saveInsightCall = ({ updateInsightCall, info, fields, content, newFileName, newGroupName, useNewGroupAsOld = false, tags = undefined }) =>
    updateInsightCall({
      content,
      fields,
      isWorkflow: isWorkflow(content),
      newGroupName,
      newInsightName: newFileName,
      oldGroupName: useNewGroupAsOld ? newGroupName : info.groupName,
      oldInsightName: useNewGroupAsOld ? newFileName : info.fileName,
      tags
    })
    .then(({ fileId }) => fileId)
      
  const createNewInsightCall = ({ name, groupName, description, content, tags, createInsightCall, uploadFileContentCall, image, sourceName, sourceGroup, sourceVersion, isDraft = false, shouldCreateGroup = false }) => 
    createInsightCall({
      content,
      description,
      groupName,
      insightName: name,
      isWorkflow: isWorkflow(content),
      shouldCreateGroup,
      sourceName,
      sourceGroup,
      sourceVersion,
      isDraft,
      tags,
      type: content.type,
      useUserToken: true
    })
    .then(({ logoLocation, id }) => 
        uploadFileContentCall({ url: logoLocation, image })
          .then(() => id)
      )
      
  const uploadLogo = ({ uploadFileMetadataCall, uploadFileContentCall, newGroupName, fields, image }) => {
    const uploadLogoCall = () =>
      uploadFileMetadataCall({ 
        fileName: image.name,
        groupName: newGroupName,
        fileType: DataLakeEnums.FILE_TYPE.SOURCE
      })
    const uploadLogoS3Call = response =>
      uploadFileContentCall({
        url: response.location,
        image
      })
      .then(() => response.fileId)

    return uploadLogoCall().then(uploadLogoS3Call).then(logoId => updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.LOGO, value: logoId }))
  }
  
  const prepareTextTiles = ({ insightName, content, filesToSave, newGroupName }) => {
    const localContent = Object.assign({}, content)
    localContent.tiles = content.tiles.slice().map(tile => Object.assign({}, tile))
    // when file is not from DL, we are saving it to insight group with generated name
    localContent.tiles.filter(tile => tile.type === basicCustomInsightConsts.TILE_TYPES.TEXT && tile?.source?.value?.editorContent && tile?.source?.type === basicCustomInsightConsts.TILE_SOURCE_TYPES.EDITOR).forEach(tile => {
      filesToSave.push({ groupName: newGroupName, name: `${insightName}_${tile.title || "TextTile"}.md`, fileContent: tile?.source?.value?.editorContent, fileType: DataLakeEnums.FILE_TYPE.MARKDOWN })
      tile.source.value.name = `${insightName}_${tile.title || "TextTile"}.md`
      tile.source.value.groupName = newGroupName;
      tile.source.value.editorContent = undefined
    })
    return Promise.resolve(localContent)
  }

  const prepareCodeTiles = ({ insightName, content, filesToSave, newGroupName }) => {
    const localContent = Object.assign({}, content)
    localContent.tiles = content.tiles.slice().map(tile => Object.assign({}, tile))
    // when file is not from DL, we are saving it to insight group with generated name
    localContent.tiles.filter(tile => tile?.type === basicCustomInsightConsts.TILE_TYPES.CODE && tile?.source?.type === basicCustomInsightConsts.TILE_SOURCE_TYPES.EDITOR).forEach(tile => {
      tile.source.value.name = `${insightName}_${tile.title || "CodeTile"}.${tile.codeType === basicCustomInsightConsts.CODE_TYPES.HTML.value ? "ftl" : tile.codeType}`
      tile.source.value.groupName = newGroupName
      const item = { 
        groupName: tile.source.value.groupName,
        name: tile.source.value.name,
        fileContent: tile?.source?.value?.editorContent,
        fileType: DataLakeEnums.FILE_TYPE.SOURCE,
        contentType: "text/plain"
      }

      filesToSave.push(item)
      tile.isFtlActive = undefined; // it is important to reset this param, to ensure that edit/preview mode will work ok
      tile.templateId = undefined;
      tile.content = undefined;
      tile.ftlContent = undefined; // backward comp
      delete tile.fileName
    })

    return Promise.resolve(localContent)
  }

  const prepareDiagramTiles = ({ content }) => {
    const localContent = Object.assign({}, content)
    localContent.tiles = content.tiles.slice().map(tile => Object.assign({}, tile))
    localContent.tiles.filter(tile => tile.type === basicCustomInsightConsts.TILE_TYPES.DIAGRAM).forEach(tile => {
      delete tile.diagram;
      return tile;
    })
    return localContent;
  }

  const preparePartners = ({ insightName, content, filesToSave, newGroupName, tilePartnerAssets = {} }) => {
    const localContent = Object.assign({}, content)
    localContent.tiles = content.tiles.slice().map(tile => Object.assign({}, tile, { partners: (tile.partners || []).map(partner => Object.assign({}, partner)) }))
    localContent.tiles.filter(tile => tile.partners?.length > 0).forEach(tile => {
      tile.partners?.filter(partner => !partner.fileName && partner.name).forEach(partner => {
        const isEngineTile = basicCustomInsightConsts.ENGINE_TILE_TYPES.includes(tile.type),
              fileContent = isEngineTile ? tilePartnerAssets[tile.id]?.[partner.id]?.file : partner.image
        if(!isEngineTile || fileContent) {
          filesToSave.push({ groupName: newGroupName, name: `${insightName}_${tile.title}_${partner.name}`, fileContent, fileType: DataLakeEnums.FILE_TYPE.SOURCE })
          partner.fid = undefined
          partner.fileName = `${insightName}_${tile.title}_${partner.name}`;
          partner.imageUrl = undefined;
          partner.image = undefined;
        }
      })
      tile.partners?.filter(partner => partner.fileName).forEach(partner => {
        partner.imageUrl = undefined;
        partner.image = undefined;
      })
    })
    return Promise.resolve(localContent)
  }

  const prepareTemplateFiles = ({ content, groupName, insightName, templatesToSave }) => {
    const localContent = Object.assign({}, content)
    localContent.tiles = content.tiles.slice().map(tile => Object.assign({}, tile))
    const checkAndPush = item => {
      if(templatesToSave.findIndex(t => t.name === item.name && t.groupName === item.groupName) === -1) {
        templatesToSave.push(item)
      }
    }
    localContent.tiles.filter(tile => tile.templateContent).forEach(tile => {
      if(tile.type === TILE_TYPES.TABLE) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.csv`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "text/csv", paramName: tile.templateContent }))
      } else if(tile.type === TILE_TYPES.CHART) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.csv`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "text/csv", paramName: tile.templateContent }))
      } else if(tile.type === TILE_TYPES.TEXT) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.md`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "text/md", paramName: tile.templateContent }))
      } else if(tile.type === TILE_TYPES.PDF) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.pdf`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "application/pdf", paramName: tile.templateContent }))
      } else if(tile.type === TILE_TYPES.IMAGE) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.jpg`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "image/jpg", paramName: tile.templateContent }))
      } else if(tile.type === TILE_TYPES.CODE) {
        tile.source = {
          type: basicCustomInsightConsts.TILE_SOURCE_TYPES.FILE,
          value: {
            name: `${insightName}_${tile.templateContent}.${tile.codeType === basicCustomInsightConsts.CODE_TYPES.HTML.value ? "ftl" : tile.codeType}`,
            groupName
          }
        }
        checkAndPush(ReduxService.updateObject(tile.source.value, { contentType: "text/html", paramName: tile.templateContent }))
      }
      delete tile.templateContent;
    })
    Object.values(localContent.inputParams || {}).filter(ip => ip.templateContent).forEach(ip => {
      ip.csvFile = {
        name: `${insightName}_${ip.templateContent}.csv`,
        groupName
      }
      checkAndPush(ReduxService.updateObject(ip.csvFile, { contentType: "text/csv", paramName: ip.templateContent }))
      delete ip.templateContent;
    })
    return localContent;
  }

  const saveFile = ({ uploadFileMetadataCall, uploadFileContentCall, item: { name, groupName, contentType = undefined, fileContent = undefined, fileType = DataLakeEnums.FILE_TYPE.SOURCE }, content }) => {
    const metadataCall = uploadFileMetadataCall({ fileName: name, groupName, fileType })
    const fileCall = response => uploadFileContentCall({ image: fileContent || content, url: response.location, contentType })
    return metadataCall.then(fileCall)
  }

  const saveFiles = ({ uploadFileMetadataCall, uploadFileContentCall, filesToSave, setErrors }) => 
    Promise.all(filesToSave.map(item => saveFile({ item, setErrors, uploadFileMetadataCall, uploadFileContentCall })))
  

  const parseCsvContent = ({ columns = [], data } = {}) => {
    if(columns.length === 0) {
      return "[]";
    }
    const headersRow = unparse({
      fields: columns,
      data: []
    })
    const unparsedCsv = unparse(data, { 
      columns,
      header: false
    })
    return headersRow + unparsedCsv
  }

  const saveTemplateFiles = ({ uploadFileMetadataCall, uploadFileContentCall, templatesToSave, templateFiles }) =>
    Promise.all(templatesToSave.map(item => {
      const content = item.contentType === "text/csv" ? parseCsvContent(templateFiles[item.paramName]) : templateFiles[item.paramName]
      return saveFile({ item, content, uploadFileMetadataCall, uploadFileContentCall })
    }))

  const reassignTheGroups = ({ name, groupName, assignRolesCall, getPipelineCall }) => dispatch =>
    dispatch(basicCustomInsightActions.getInsightRoleGroups({ getPipelineCall }))
      .then(groupsObj => {
        const groups = {}
        Object.entries(groupsObj).filter(([,v]) => !v.isWorkflow).forEach(([k,]) => {
          groups[k] = "R"
        })
        return groups;
      })
      .then(groups => assignRolesCall({ insightName: name, groupName, groups })
          .then(({ createdGroupsEntitlements, removedNotUsedGroupsEntitlements, updatedGroupsEntitlements }) => {
            console.info("Entitlements have been updated:", {
              created: createdGroupsEntitlements,
              removed: removedNotUsedGroupsEntitlements,
              updated: updatedGroupsEntitlements
            })
          })
      )

  const saveInsightCore = ({ navigate, name: defaultName, groupName: defaultGroup, fields: defaultFields, tags: defaultTags, description: defaultDesc, image, closeDialog, shouldCreateGroup = false, asNew = false, isDraft = false, useNewGroupAsOld = false, colorScheme, assignRolesCall, getPipelineCall, createInsightCall, updateInsightCall, uploadFileMetadataCall, uploadFileContentCall }) => (dispatch, getState) => {
    const customState = getState().insightState.customState,
          currentInsightGroup = customState.info?.groupName,
          groupName = defaultGroup || currentInsightGroup,
          currentInsightName = String(customState.info?.fileName).replace(/.json/g, ""),
          nameWithoutJson = defaultName || currentInsightName,
          name = `${nameWithoutJson}.json`,
          tags = defaultTags || customState.info?.fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.TAGS)?.value?.split(" ").map(v => decodeURI(v)),
          description = defaultDesc || customState.info?.fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.DESCRIPTION)?.value,
          templateFiles = customState.templateParams,
          baseFields = defaultFields || customState.info?.fields,
          { info, content: contentFromState } = getState().insightState.customState,
          currentInsightVersion = customState.info?.version,
          templatesToSave = [],
          filesToSave = []

    let content = contentFromState || basicCustomInsightConsts.INIT_CONTENT
    content = ReduxService.updateObject(content, { colorScheme })

    dispatch(basicCustomInsightActions.onChange(true, "isSaving"))
    let fields = updateInfoFields({ fields: baseFields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.TAGS, value: tags?.join(" ") })
        fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.DESCRIPTION, value: description })
        fields = isDraft ? (() => {
          fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.DRAFT, value: true })
          fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_GROUP, value: currentInsightGroup })
          fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_NAME, value: currentInsightName })
          fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_VERSION, value: currentInsightVersion })
        })() : fields
    let localContent;

    return prepareTextTiles({ insightName: nameWithoutJson, content, filesToSave, newGroupName: groupName })
      .then(clonedContent => prepareCodeTiles({ insightName: nameWithoutJson, content: clonedContent, filesToSave, newGroupName: groupName }))
      .then(clonedContent => prepareDiagramTiles(({ content: clonedContent })))
      .then(clonedContent => preparePartners(({ insightName: nameWithoutJson, content: clonedContent, filesToSave, newGroupName: groupName, tilePartnerAssets: customState.tilePartnerAssets })))
      .then(clonedContent => prepareTemplateFiles(({ insightName: nameWithoutJson, groupName, content: clonedContent, templatesToSave, filesToSave })))
      .then(clonedContent => {
        localContent = clonedContent;
        if(asNew) {
          return createNewInsightCall({ 
            createInsightCall, 
            uploadFileContentCall,
            name, 
            groupName,
            description, 
            content: clonedContent, 
            isDraft,
            sourceName: currentInsightName,
            sourceGroup: currentInsightGroup,
            sourceVersion: currentInsightVersion,
            tags,
            image,
            shouldCreateGroup
          })
        } else {
          const saveCallLocal = newFields => saveInsightCall({ 
            updateInsightCall,
            info,
            fields: newFields, 
            content: clonedContent, 
            newFileName: name, 
            newGroupName: groupName, 
            useNewGroupAsOld,
            tags
          })
          if(image) {
            return uploadLogo({ uploadFileContentCall, uploadFileMetadataCall, newGroupName: groupName, fields, image })
              .then(newFields => {
                fields = updateInfoFields({ fields, name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.LOGO, value: newFields.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.LOGO)?.value })
                return saveCallLocal(newFields)
              })
          } else {
            return saveCallLocal(fields)
          }
        }
      })
      .then(() => saveTemplateFiles({ uploadFileMetadataCall, uploadFileContentCall, templatesToSave, templateFiles }))
      .then(() => saveFiles({ uploadFileMetadataCall, uploadFileContentCall, filesToSave }))
      .then(() => dispatch(reassignTheGroups({ name, groupName, assignRolesCall, getPipelineCall })))
      .then(id => {
        dispatch(basicCustomInsightActions.onChange(false, "createDialogIsOpen"))
        dispatch(basicCustomInsightActions.onChange(false, 'editActive'))
        dispatch(basicCustomInsightActions.clearTilePartnerAssets())
        return id;
      })
      .then(id => {
        const localInfo = ReduxService.updateObject(info, { fields, fid: id, fileName: name, groupName })
        localContent.tiles.forEach(tile => {
          if(tile.type === basicCustomInsightConsts.TILE_TYPES.DIAGRAM){
            tile.diagram = content.tiles.filter(tile => tile.type === basicCustomInsightConsts.TILE_TYPES.DIAGRAM)?.find(diagramTile => diagramTile.id === tile.id)?.diagram
          }
        })
        dispatch(basicCustomInsightActions.onChange(localInfo, 'initInfo'))
        dispatch(basicCustomInsightActions.onChange(localInfo, 'info'))
        dispatch(basicCustomInsightActions.onChange(localContent, 'initContent'))
        dispatch(basicCustomInsightActions.onChange(localContent, 'content'))
        const url = DashboardLinkFactory.insight.view({ baseUrl: "", groupName, name: nameWithoutJson })
        if(decodeURI(window.location.pathname) !== url) {
          navigate(url, { replace: true })
        }
        closeDialog?.()
        return id
      })
      .finally(() => dispatch(basicCustomInsightActions.onChange(false, "isSaving")))
  }

  const saveNewInsight = args => dispatch => dispatch(saveInsightCore(args))
    .then(() => dispatch(globalActions.getMessageStore()).success("Insight has been created."))

  const saveInsight = args => dispatch => 
    dispatch(saveInsightCore(args))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Insight has been saved.")
        return response;
      })

  const moveInsight = args => dispatch => 
    dispatch(saveInsightCore({ ...args, asNew: false }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Insight has been moved to the new group.")
        return response;
      })

  const copyInsight = ({ navigate, groupName, name, tags, closeDialog, isDraft = false, assignRolesCall, getPipelineCall, createInsightCall, updateInsightCall, uploadFileMetadataCall, uploadFileContentCall, getFileContentByIdCall }) => (dispatch, getState) => {
    const logoId = getState().insightState.customState.info?.fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.LOGO)?.value
    return getFileContentByIdCall({ id: logoId })
      .then(response => response.blob())
      .then(data => new File([data], `${name}.jpg`, { type: 'image/jpeg' }))
      .then(image => 
        dispatch(saveInsightCore({ createInsightCall, updateInsightCall, uploadFileMetadataCall, uploadFileContentCall, navigate, name, groupName, tags, image, asNew: true, closeDialog, isDraft, assignRolesCall, getPipelineCall }))
          .then(response => {
            dispatch(globalActions.getMessageStore()).success("New Insight has been created.")
            return response;
          })
      )
  }

  const editInsight = args => dispatch => 
    dispatch(saveInsightCore({ ...args, asNew: false }))
      .then(response => {
        dispatch(globalActions.getMessageStore()).success("Insight has been saved.")
        return response;
      })

  const publishInsight = args => (dispatch, getState) => {
    const insightName = getState().insightState.customState.info?.fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_NAME)?.value,
          insightGroup = getState().insightState.customState.info?.fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_GROUP)?.value,
          fields = getState().insightState.customState.info?.fields?.filter(f => f.name !== basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_NAME && f.name !== basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_GROUP && f.name !== basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_VERSION && f.name !== basicCustomInsightConsts.INSIGHT_FILE_FIELDS.DRAFT)
    return dispatch(saveInsightCore({ name: insightName, groupName: insightGroup, fields, asNew: false, useNewGroupAsOld: true, ...args }))
  }

  const getFileFields = ({
    userName,
    insightName,
    insightGroupName,
    insightVersion = "",
    title,
    shouldShowCorrections = false
  }) => [{
    name: "Saved by",
    value: userName
  }, {
    name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_NAME,
    value: insightName
  }, {
    name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_GROUP,
    value: insightGroupName
  }, {
    name: basicCustomInsightConsts.INSIGHT_FILE_FIELDS.SOURCE_VERSION,
    value: insightVersion
  }, {
    name: "Source type",
    value: "Insight"
  }, ...(shouldShowCorrections ? [{ name: "Corrections", value: true }] : []), {
    name: "Tile",
    value: title
  }]

  const applyDate = fileName => (_, getState) => {
    const definedTime = getState().myAccountState.settingsState.definedTime
    let localFileName = fileName.replace("${currentMillis}", TimeService.utcDateToMillis());
    const matches = localFileName.match(/\$\{.*\}/g)
    if(!matches || matches.length === 0) {
      return localFileName;
    } else {
      matches.forEach(match => {
        localFileName = localFileName.replace(match, TimeService.getUserFormattedDateTime(undefined, definedTime, TimeService.translateFormat(match.replace("${", "").replace("}", ""))))
      })
    }
    return localFileName;
  }

  const saveFileFromInsight = ({ uploadFileContent, uploadFileMetadata, groupName, fileName, fileType, tileTitle, content, insightName, insightVersion, shouldShowCorrections, withStatusBox = true }) => (dispatch, getState) => {
    const userName = getState().myAccountState.settingsState.userName,
          { groupName: insightGroupName } = getState().insightState.customState.info || {}
    const nameWithAppliedDate = dispatch(applyDate(fileName))

    return uploadFileMetadata({ groupName, fileName, fileType, fields: getFileFields({
            userName,
            insightName,
            insightGroupName,
            insightVersion,
            tileTitle,
            shouldShowCorrections
          })
        })
        .then(response => {
          const jobId = response.headers.get("X-KH-JOB-ID")
          if(withStatusBox) {
            dispatch(statusBoxActions.addJob({
              jobId, 
              displayName: nameWithAppliedDate, 
              subtitle: tileTitle,
              isDisabled: false,
              type: StatusesEnums.STATUS_TYPE.ETL,
            }))
          }
          return response
        })
        .then(response => response.json())
        .then(response => uploadFileContent({
          url: response.location,
          image: content
        }))
        .then(() => dispatch(globalActions.getMessageStore()).success("File has been saved."))
        .then(() => ({ name: fileName, groupName }))
  }

  const getInsightNameValidators = (errors = []) => [{
    message: errors?.find(e => e.field === "name")?.message || "",
    validator: () => errors?.findIndex(e => e.field === "name") === -1
  }]

  const getGroupNameValidators = (errors = []) => [{
    message: errors?.find(e => e.field === "groupName")?.message || "",
    validator: () => errors?.findIndex(e => e.field === "groupName") === -1
  }]

  const getTagsValidators = (errors = []) => [{
    message: errors?.find(e => String(e.field).includes("tags"))?.message || "",
    validator: () => errors?.findIndex(e => String(e.field).includes("tags")) === -1
  }]

  const getDescValidators = (errors = []) => [{
    message: errors?.find(e => String(e.field).includes("description"))?.message || "",
    validator: () => errors?.findIndex(e => String(e.field).includes("description")) === -1
  }]

  return {
    saveInsight,
    saveNewInsight,
    saveFileFromInsight,
    moveInsight,
    copyInsight,
    editInsight,
    publishInsight,
    getInsightNameValidators,
    getGroupNameValidators,
    getTagsValidators,
    getDescValidators,
    onValidationHandler,
    reassignTheGroups,
    getFileFields
  }
})()
